import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService, DeputyService, EmulationService, GetImageWithGidService } from '@coin/shared/auth/data-access';
import { DirectsState } from '../../data/directs.state';
import { FirstVisitService } from '../../data/first-visit.service';
import { TokenService } from '../../data/token.service';
import {
  CmsTranslationService,
  FeatureFlagsService,
  LoadingService,
  SetUserImageService,
  StorageService
} from '@coin/shared/core/data-access';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { ContentLanguage, FrontendType, PermissionResource, StorageKey } from '@coin/shared/core/util-enums';
import { GlobalEventsService } from '@coin/shared/core/util-helpers';
import {
  CoinUser,
  Employee,
  EmployeeDto,
  EmployeeSlim,
  LanguageMapping,
  UserMenuItem
} from '@coin/shared/core/util-models';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { SelectEmulatePersonComponent } from '../select-emulate-person/select-emulate-person.component';
import { ManageDeputiesComponent } from '../manage-deputies/manage-deputies.component';
import { ToastrService } from 'ngx-toastr';
import { UserState } from '../../data/user.state';
import { PermissionsService } from '../../data/permissions.service';
import { TranslationViewerService } from '../../data/translation-viewer.service';

@Component({
  selector: 'coin-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UserMenuComponent implements OnInit {
  private eRef = inject(ElementRef);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private firstVisit = inject(FirstVisitService);
  private router = inject(Router);
  private getImage = inject(GetImageWithGidService);
  private dialog = inject(MatDialog);
  private emulationService = inject(EmulationService);
  private storageService = inject(StorageService);
  private cmsTranslationService = inject(CmsTranslationService);
  private events = inject(GlobalEventsService);
  private setUserImageService = inject(SetUserImageService);
  token = inject(TokenService);
  private userState = inject(UserState);
  private deputyService = inject(DeputyService);
  private loadingService = inject(LoadingService);
  private featureFlagService = inject(FeatureFlagsService);
  private permissionService = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastrService);
  public readonly translationViewer = inject(TranslationViewerService);
  private directsState = inject(DirectsState);

  @Input() isDeputyAllowed = true;
  @Input() isProfileAllowed = true;

  // user info
  public user = signal<CoinUser>(undefined);
  public userDetails = signal<Employee>(undefined); // used for showing org code & place of action
  public userImageSrc = signal<string>(undefined);
  public isEmulated = signal<boolean>(undefined);
  public isLoggedInAsDeputy = signal<boolean>(undefined);

  public menuItems = signal<UserMenuItem[]>([]);
  public showMenu = signal(false);
  public selectableLangs = signal<LanguageMapping>({});
  public languageSelected = signal(ContentLanguage.ENGLISH);
  public remainingTimeHovered = signal(false);
  public deputies = signal<EmployeeSlim[]>([]);
  public isDeputyListVisible = signal(false);
  public languageSubmenuVisible = signal(false);
  public deputySubmenuVisible = signal(false);

  clickout = (event: MouseEvent): void => {
    if (
      !this.eRef.nativeElement.contains(event.target) &&
      !(event.target as HTMLElement).closest('.deputy-menu-item')
    ) {
      if (this.showMenu() && !this.firstVisit.status()) {
        this.openMenu();
        this.closeDeputyMenu();
      }
    }
  };

  public get isAdmin(): boolean {
    return this.permissionService.isSuperAdmin();
  }

  public get isDeputyMenuEntryShown(): boolean {
    return (
      this.isDeputyAllowed &&
      (!this.isEmulated() || this.isLoggedInAsDeputy()) &&
      this.featureFlagService.isActive('deputies')
    );
  }

  public get emulationEnabled(): boolean {
    return this.permissionService.hasPermissions(PermissionResource.Emulation);
  }

  constructor() {
    this.token.init(ConfirmationDialogComponent);
  }

  async ngOnInit(): Promise<void> {
    this.getLangs();
    this.listenToLoggedInUser();
    this.token.startCheckingTimeTillLogout();
    await this.checkEmulationValidity();
    this.isLoggedInAsDeputy.set((await this.authService.getEmulationDecoded())?.IsDeputy);
    this.events
      .listen('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.clickout(event));
  }

  private async checkEmulationValidity(): Promise<void> {
    const token = await this.storageService.getAsync(StorageKey.EMULATION_TOKEN);

    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const issuedAt = payload.iat;
      const now = Math.floor(Date.now() / 1000);

      const oneHour = 3600;
      const isOlderThanOneHour = now - issuedAt > oneHour;

      if (isOlderThanOneHour) {
        this.authService.clearEmulation();
        this.isEmulated.set(false);
      } else {
        this.isEmulated.set(true);
      }
    } else {
      this.isEmulated.set(false);
    }
  }

  private getLangs(): void {
    this.cmsTranslationService
      .getTranslationConfigFile(FrontendType.customer)
      .pipe(
        filter(selectableLangs => !!selectableLangs),
        tap(selectableLangs => {
          this.selectableLangs.set(selectableLangs);
        }),
        switchMap(() => this.storageService.getAsync(StorageKey.LANGUAGE)),
        tap(lang => {
          this.languageSelected.set(lang);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
      this.storageService.setAsync(StorageKey.LANGUAGE, item.lang as ContentLanguage);
      this.languageSelected.set(item.lang as ContentLanguage);
    });
  }

  private listenToLoggedInUser(): void {
    this.userState.user$
      .pipe(
        filter(user => !!user),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(user => {
        this.user.set(user);
        this.updateUserInfo();
      });
  }

  private updateUserInfo(): void {
    combineLatest([this.userState.currentUserDetails$, this.getImage.getImageWithStore(this.user().gid)])
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(([userDetails, image]) => {
        this.userDetails.set(userDetails);
        this.userImageSrc.set(image);
      });
  }

  public openMenu(): void {
    this.showMenu.set(!this.showMenu());
    this.languageSubmenuVisible.set(false);
    this.deputySubmenuVisible.set(false);
  }

  public changeLanguage(lang: ContentLanguage): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          headline: 'general.info',
          translate: true,
          msg: `general.may-need-reload-for-changes`,
          confirmMsg: 'general.btnProceed',
          cancelMsg: 'general.cancel'
        }
      })
      .afterClosed()
      .pipe(
        filter(isConfirm => !!isConfirm),
        tap(() => {
          this.translate.use(lang);
          this.openMenu();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public profileClicked(): void {
    // currently the employee profile is disabled on PROD, so just use old behaviour in that case
    this.router.navigate(['/employee-detail-overview']);
  }

  public profilePictureClicked(): void {
    this.setUserImageService.setPersonalPicture();
  }

  public languageChangeClicked(): void {
    this.languageSubmenuVisible.set(!this.languageSubmenuVisible());
  }

  public logoutClicked(): void {
    this.user.set(undefined);
    this.router.navigate(['/logout']);
  }

  public emulateClicked(): void {
    if (!this.isEmulated()) {
      this.dialog
        .open(SelectEmulatePersonComponent)
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(emulateUser => {
          if (emulateUser) {
            this.getNewTokenForEmulationUser(emulateUser, 'Emulation');
          }
        });
    } else {
      this.removeEmulation();
    }
  }

  public manageDeputiesClicked(): void {
    this.deputySubmenuVisible.set(!this.deputySubmenuVisible());
  }

  get hasDirects(): boolean {
    return this.directsState.hasDirects();
  }

  public onRightClick(e: MouseEvent): void {
    e.preventDefault();
  }

  public remainingTimeHover(state: string): void {
    if (this.isEmulated()) {
      return;
    }
    this.remainingTimeHovered.set(state === 'enter');
  }

  public async renewToken(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    await this.token.renewToken();
  }

  private getNewTokenForEmulationUser(employee: EmployeeDto, authenticationType: 'Emulation' | 'Deputy'): void {
    this.emulationService
      .emulateEmployee(employee.id, authenticationType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          setTimeout(() => {
            this.router.navigate(['/']).then(() => {
              window.location.reload();
            });
          }, 100);
        },
        error: () => {
          this.toast.error(this.translate.instant('general.emulation-error'));
        }
      });
  }

  public removeEmulation(): void {
    this.authService.clearEmulation();
    setTimeout(() => {
      this.router.navigate(['/']).then(() => {
        window.location.reload();
      });
    }, 300);
  }

  public showDeputies(): void {
    this.deputyService
      .getDeputiesForEmulation()
      .pipe(
        this.loadingService.withLoadingScreen,
        map(deputies => deputies.sort((d1, d2) => d1.firstname?.localeCompare(d2.firstname))),
        tap(data => {
          this.deputies.set(data);
        }),
        tap(() => {
          this.isDeputyListVisible.set(true);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private closeDeputyMenu(): void {
    this.deputies.set([]);
    this.isDeputyListVisible.set(false);
  }

  public emulateAsDeputy(deputy: EmployeeSlim): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          headline: 'general.info',
          translate: true,
          msg: 'deputy.emulate-msg',
          confirmMsg: 'general.btnConfirm',
          cancelMsg: 'general.btnCancel'
        }
      })
      .afterClosed()
      .pipe(
        filter(result => !!result),
        tap(() => {
          this.openMenu();
          this.closeDeputyMenu();
          this.loadingService.present(); // Will be hidden through the window reload
          this.getNewTokenForEmulationUser(deputy, 'Deputy');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public openDeputySettings(): void {
    this.openMenu();
    this.closeDeputyMenu();
    this.dialog
      .open(ManageDeputiesComponent, {
        maxWidth: '820px',
        width: '70vw',
        height: '70vh',
        maxHeight: '800px',
        minHeight: '400px'
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
